import { model as router } from '!/router'
import { AnalyticsContentType, NO_PROGRAM_DATA } from '@/constants/content'
import { formatRouterLocation } from '@/formatters/router'
import { LayoutBlockPlayer } from '@setplex/arcana'
import { Player } from '@setplex/player'
import '@setplex/player/styles.css'
import {
  api,
  type PlayerUrlError,
  type TvChannel,
  type TvChannelWithEpg,
} from '@setplex/tria-api'
import { useUnit } from 'effector-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAnalytics } from '~/app/ui/Player/useAnalytics'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { useNpaw } from '~/features/npaw'
import { model as paymentModel } from '~/features/payment/generic'
import * as pinLockFeature from '~/features/pin-lock'
import { usePlaybackUrlAdParams } from '~/features/playback-url-ad-params'
import { recentlyWatchedModel } from '~/features/recently-watched'
import { getProgramTitle } from '~/shared/formatters/tv'
import { exists } from '~/shared/helpers'
import { type CurrentProgramMediaUrl } from '~/shared/interfaces'
import { useResetToken } from '~/shared/tools/player'
import { PlayerPaymentButton } from '~/widgets/Payment'
import { PlayerSignInToWatchButton } from '~/widgets/SignInToWatch'
import { epgChannelsWidgetModel } from '~/widgets/epg/channels'
import { epgInfoWidgetModel } from '~/widgets/epg/info'
import { epgPlayerWidgetModel } from '~/widgets/epg/player'
import * as model from '../../model'
import * as epg from '../../model/epg'
import * as playerPayment from '../../model/payment'
import { $epgPlayerTranslations } from '../../model/translates'
import { featuresMaxAutoQualityModel } from '~/features/max-auto-quality'
import { model as i18nModel } from '!/i18n'

export function PlayerWindow() {
  const appLocale = useUnit(i18nModel.$locale)
  const [externalError, setExternalError] = useState<{
    code: number
    message: string
  } | null>(null)

  const selectedEpgChannel: TvChannel | null = useUnit(model.$epgActiveChannel)
  const currentMaxAutoQuality = useUnit(
    featuresMaxAutoQualityModel.$currentMaxAutoQuality
  )
  const playerNpawData = useUnit(epg.$playerNpawData)
  const { initNpaw, clearNpaw } = useNpaw(playerNpawData)

  const addCurrentChannelToRW = useUnit(recentlyWatchedModel.addTvChannelLocal)
  const onDataLoaded = () => {
    if (selectedEpgChannel) addCurrentChannelToRW(selectedEpgChannel)
  }

  const previousLocation = useUnit(router.$previous)

  const [locked, setLocked] = useState(false)

  const {
    closePinLockPopup,
    isValidPinCode,
    unlockedChannelId,
    resetValidPinCode,
    resetUnlockId,
    updateUnlockId,
    PINLockPlayerPopupComponent,
  } = pinLockFeature.usePinLockPlayerPopup(
    selectedEpgChannel?.formattedName ?? ''
  )

  const clickedEpgChannel = useUnit(epgChannelsWidgetModel.$probing)
  const updateClickedChannel = useUnit(epgChannelsWidgetModel.probe)

  const currentPlayerPageUrlError: PlayerUrlError | null =
    useUnit(api.player.$currentPlayerPageUrlError) ?? null
  const activeProgram = useUnit(epgInfoWidgetModel.$playing)

  const translates = useUnit($epgPlayerTranslations)

  const currentUrl: CurrentProgramMediaUrl | null = useUnit(
    epgPlayerWidgetModel.$url
  )

  const activeChannelList: TvChannelWithEpg[] = useUnit(epg.$channels)

  const pinFeatureIsOn = useUnit(pinLockFeature.$enabled)

  const checkSelectedEpgChannelInPlayer = useUnit(
    selectEpgChannelModel.setActiveId
  )
  const tryClosePaymentPopup = useUnit(paymentModel.tryClosePaymentPopup)
  const isOpenedPaymentPopup = useUnit(paymentModel.$isAnyPaymentPopupOpened)
  const showSignInToWatchPopup = useUnit(epg.$showSignInToWatchPopup)
  const openSignInToWatchPopupEvent = useUnit(epg.openPlayerSignInToWatchPopup)
  const showPaymentPopup = useUnit(playerPayment.$showPaymentPopup)
  const openPlayerPaymentPopupEvent = useUnit(
    playerPayment.openPlayerPaymentPopup
  )
  const paymentButtonText = useUnit(playerPayment.$paymentButtonText)

  const reloadTvPlayerUrl = useUnit(model.reloadEpgTvPlayerUrl)
  const { onPlaying, onTokenExpired } = useResetToken(reloadTvPlayerUrl)

  useEffect(() => {
    setExternalError(currentPlayerPageUrlError)
  }, [currentPlayerPageUrlError])

  const playListInfo = useMemo(() => {
    const channelsCount = activeChannelList.length
    if (!channelsCount) return

    const currentIndex = activeChannelList.findIndex(
      (channel) => channel.id === selectedEpgChannel?.id
    )
    const isFirstIndex = currentIndex === 0
    const isLastIndex = currentIndex === channelsCount - 1

    const previousIndex = isFirstIndex ? channelsCount - 1 : currentIndex - 1
    const nextIndex = isLastIndex ? 0 : currentIndex + 1

    const nextChannel = activeChannelList[nextIndex]
    const previousChannel = activeChannelList[previousIndex]

    return {
      previousChannel,
      nextChannel,
    }
  }, [selectedEpgChannel, activeChannelList])

  const isChannels = activeChannelList?.length
    ? activeChannelList.length > 1
    : null

  const handlePlay = useCallback(
    (channel?: TvChannelWithEpg | null) => {
      if (channel) {
        checkSelectedEpgChannelInPlayer(channel.id)
        updateClickedChannel(channel.id)
      }
      resetValidPinCode()
      resetUnlockId()
    },
    [
      resetUnlockId,
      resetValidPinCode,
      checkSelectedEpgChannelInPlayer,
      updateClickedChannel,
    ]
  )

  const onPlayPrevious = useCallback(() => {
    const previousChannel = playListInfo?.previousChannel
    handlePlay(previousChannel)
  }, [handlePlay, playListInfo?.previousChannel])

  const onPlayNext = useCallback(() => {
    const nextChannel = playListInfo?.nextChannel
    handlePlay(nextChannel)
  }, [handlePlay, playListInfo?.nextChannel])

  const sourceUrl =
    currentUrl?.playbackUrl && !locked && !showSignInToWatchPopup
      ? currentUrl?.playbackUrl
      : ''
  const drm = currentUrl?.drm

  const { contentId, contentType } = useMemo(() => {
    return {
      contentId: exists(selectedEpgChannel?.id)
        ? String(selectedEpgChannel?.id)
        : '',
      contentType: AnalyticsContentType.Tv,
    }
  }, [selectedEpgChannel])

  const subtitle = getProgramTitle({
    startTimeMs: activeProgram?.actualStartMs,
    programTitle: activeProgram?.programTitle,
  })

  const playerContainerRef = useRef<HTMLDivElement>(null)
  // add google pal nonce and other ad params to sourceUrl
  const playbackUrl = usePlaybackUrlAdParams(
    sourceUrl,
    playerContainerRef.current
  )

  const {
    sendAnalyticsDataStartEventCallback,
    sendAnalyticsDataStopEventCallback,
    sendAnalyticsContentWatchingCallback,
  } = useAnalytics({
    sourceUrl: playbackUrl,
    contentId,
    contentType,
    contentTitle: activeProgram?.programTitle,
    contentOpenFrom: formatRouterLocation({
      pathname: previousLocation?.pathname,
    }),
    contentProgram: subtitle || NO_PROGRAM_DATA,
  })

  const onReturnToPrevPageClick = useCallback(() => {
    closePinLockPopup()
    if (isOpenedPaymentPopup) tryClosePaymentPopup()
    if (document.fullscreenElement) {
      return document.exitFullscreen()
    }
    // @ts-ignore: TS2551 because this fix only Safari
    if (document.webkitFullscreenElement) {
      // @ts-ignore: TS2551 because this fix only Safari
      return document.webkitExitFullscreen()
    }
  }, [tryClosePaymentPopup, closePinLockPopup, isOpenedPaymentPopup])

  const loadUrl = useUnit(epgPlayerWidgetModel.fetchUrl)

  const rewindTo = useCallback(
    (isRewind?: boolean) => {
      if (!activeProgram) return
      const { startTime } = activeProgram
      loadUrl({
        channelId: Number(contentId),
        startTime,
        isRewind,
      })
    },
    [activeProgram, contentId, loadUrl]
  )

  useEffect(() => {
    if (pinFeatureIsOn) {
      resetValidPinCode()
      const lockedChannel =
        (unlockedChannelId !== selectedEpgChannel?.id &&
          selectedEpgChannel?.locked) ||
        false

      setLocked(lockedChannel)
    }
  }, [selectedEpgChannel, unlockedChannelId, resetValidPinCode, pinFeatureIsOn])

  useEffect(() => {
    if (isValidPinCode && selectedEpgChannel?.id && pinFeatureIsOn) {
      const channel = clickedEpgChannel
      const unlockId =
        channel?.id &&
        (locked ||
          unlockedChannelId === channel?.id ||
          !(channel?.free || channel?.purchaseInfo))
          ? channel.id
          : 0

      setLocked(!isValidPinCode)
      updateUnlockId(unlockId)
    }
  }, [
    isValidPinCode,
    selectedEpgChannel,
    clickedEpgChannel,
    updateUnlockId,
    locked,
    unlockedChannelId,
    pinFeatureIsOn,
  ])

  useEffect(() => {
    if (!pinFeatureIsOn) setLocked(false)
  }, [pinFeatureIsOn])

  const playerDisabled = locked || showSignInToWatchPopup || showPaymentPopup

  const [isFullscreen, setFullscreenChange] = useState(false)

  useEffect(() => {
    // if user leaves page, lock current unlocked channel
    const handleBeforeUnload = () => {
      resetUnlockId()
      resetValidPinCode()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [resetUnlockId, resetValidPinCode])

  return (
    <LayoutBlockPlayer ref={playerContainerRef} miniPlayer={true}>
      <Player
        isRestrictedMode={true}
        id={selectedEpgChannel?.id}
        contentId={String(selectedEpgChannel?.id)}
        title={selectedEpgChannel?.formattedName}
        subtitle={subtitle}
        translates={translates}
        onReady={initNpaw}
        onDispose={clearNpaw}
        onPlayNext={isChannels ? onPlayNext : undefined}
        onPlayPrevious={isChannels ? onPlayPrevious : undefined}
        onAnalyticsStartPlaying={sendAnalyticsDataStartEventCallback}
        onAnalyticsStopPausePlaying={sendAnalyticsDataStopEventCallback}
        onAnalyticsContentWatching={sendAnalyticsContentWatchingCallback}
        onDataLoaded={onDataLoaded}
        sourceUrl={playbackUrl}
        drm={drm}
        isWindowMode={true}
        externalError={externalError}
        onReturnToPrevPageClick={onReturnToPrevPageClick}
        muted={true}
        isLiveUi
        isLiveRewindAllowed={Boolean(selectedEpgChannel?.liveRewind)}
        streamTimeStart={activeProgram && activeProgram.startTime}
        rewindStartStamp={currentUrl?.timeShift}
        streamTimeEnd={currentUrl?.endTime}
        rewindTo={rewindTo}
        disabled={Boolean(playerDisabled)}
        // 'https://cph-p2p-msl.akamaized.net/hls/live/2000341/test/master.m3u8'
        onFullscreenChange={setFullscreenChange}
        onPlaying={onPlaying}
        onTokenExpired={onTokenExpired}
        isDolbyAtmos
        maxAutoQuality={currentMaxAutoQuality}
        appLocale={appLocale}
      >
        {isFullscreen && (
          <>
            {showPaymentPopup && !locked && (
              <PlayerPaymentButton
                paymentButtonText={paymentButtonText}
                openPlayerPaymentPopupEvent={openPlayerPaymentPopupEvent}
              />
            )}
            {showSignInToWatchPopup && (
              <PlayerSignInToWatchButton
                openSignInToWatchPopupEvent={openSignInToWatchPopupEvent}
              />
            )}
            {locked && !showSignInToWatchPopup && (
              <PINLockPlayerPopupComponent key={selectedEpgChannel?.id} />
            )}
          </>
        )}
      </Player>
    </LayoutBlockPlayer>
  )
}
